// Flow 1 - the verdict pack screen (docs/phase-0/slice5-ux-flows.md §4.3-4.4). Route
// `/apps/new/glim/verdict` (wired by Wave 4). Polls the canonical
// `GET /v1/apps/:appId/validate/verdict` (packages/shared/src/http/validate.ts:32) every
// ~10s while researching (ux spec §4.4's own convention) - a `not_found` boundary code is
// read as "still researching" (the same not_found-as-not-ready convention
// `src/api/knowledge.ts`/AskCrew.tsx already established, ASSUMED/flagged: no canon doc
// confirms this exact polling semantics, since no distinct "pending" member exists on the
// `VerdictPack` wire shape - only `verdict` and `insufficient_evidence`).
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { isErr } from "@nightcrew/shared";
import type { VerdictPack } from "@nightcrew/shared";
import { FocusedFlowShell } from "../../components/FocusedFlowShell.js";
import { DeniedCard } from "../../components/DeniedCard.js";
import { NotYetValidatedDisclosure } from "../../components/NotYetValidatedDisclosure.js";
import { VerdictPackView } from "../../components/VerdictPackView.js";
import { getApp } from "../../api/apps.js";
import { getVerdict } from "../../api/validate.js";
import { isBoundaryCode } from "../../api/errors.js";
import { isOwnerOrAdmin, useSession } from "../../state/session.js";
import { useWizard } from "../../state/wizard.js";
import { validateCopy } from "../../copy/validate.js";

const POLL_INTERVAL_MS = 10_000;

type PackLoad =
  | { readonly status: "loading" }
  | { readonly status: "researching" }
  | { readonly status: "error" }
  | { readonly status: "ready"; readonly pack: VerdictPack };

type AppLoad =
  | { readonly status: "loading" }
  | { readonly status: "denied" }
  | { readonly status: "ready"; readonly name: string };

export const VerdictScreen = (): React.JSX.Element => {
  const navigate = useNavigate();
  const { draft } = useWizard();
  const { state: session } = useSession();
  const appId = draft.appId;

  const [appLoad, setAppLoad] = useState<AppLoad>({ status: "loading" });
  const [packLoad, setPackLoad] = useState<PackLoad>({ status: "loading" });
  const [notYetValidatedStamped, setNotYetValidatedStamped] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (appId === null) return;
    const cancelled = { current: false };
    void (async () => {
      const result = await getApp(appId);
      if (cancelled.current) return;
      if (isErr(result)) {
        setAppLoad({ status: "denied" });
        return;
      }
      setAppLoad({ status: "ready", name: result.value.name });
    })();
    return () => {
      cancelled.current = true;
    };
  }, [appId]);

  useEffect(() => {
    if (appId === null) return;

    const clearPoll = (): void => {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    const cancelled = { current: false };
    const poll = async (): Promise<void> => {
      const result = await getVerdict(appId);
      if (cancelled.current) return;
      if (isErr(result)) {
        if (isBoundaryCode(result.error, "not_found")) {
          setPackLoad({ status: "researching" });
          return;
        }
        setPackLoad({ status: "error" });
        clearPoll();
        return;
      }
      setPackLoad({ status: "ready", pack: result.value });
      clearPoll();
    };

    setPackLoad({ status: "loading" });
    void poll();
    intervalRef.current = setInterval(() => {
      void poll();
    }, POLL_INTERVAL_MS);

    return () => {
      cancelled.current = true;
      clearPoll();
    };
  }, [appId, attempt]);

  if (appId === null) {
    return (
      <FocusedFlowShell title={validateCopy("validate.verdict.heading")}>
        <div className="stack gap12">
          <div className="skeleton skeleton-line" style={{ width: "40%" }} />
          <div className="card skeleton-card" />
        </div>
      </FocusedFlowShell>
    );
  }

  if (appLoad.status === "denied") {
    return (
      <FocusedFlowShell title={validateCopy("validate.verdict.heading")}>
        <DeniedCard
          title="We can't find that app"
          message="It may not exist, or it isn't on your account."
        />
      </FocusedFlowShell>
    );
  }

  const appName = appLoad.status === "ready" ? appLoad.name : "your app";

  return (
    <FocusedFlowShell
      title={validateCopy("validate.verdict.heading")}
      onBack={() => {
        void navigate("/apps/new/glim");
      }}
    >
      <h1 className="h2">{validateCopy("validate.verdict.heading")}</h1>

      {packLoad.status === "loading" || packLoad.status === "researching" ? (
        <div className="card pad mt16" data-testid="verdict-researching">
          <div className="meter bordered">
            <span style={{ width: "45%" }} />
          </div>
          <p className="muted mt8" role="status" style={{ fontSize: 12.5 }}>
            {validateCopy("validate.verdict.researching")}
          </p>
        </div>
      ) : packLoad.status === "error" ? (
        <div className="card state-card mt16">
          <p>{validateCopy("validate.verdict.researchFailed")}</p>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              setAttempt((a) => a + 1);
            }}
          >
            {validateCopy("validate.verdict.retryCta")}
          </button>
        </div>
      ) : (
        <div className="mt16">
          {notYetValidatedStamped ? <NotYetValidatedDisclosure appName={appName} /> : null}
          <div className={notYetValidatedStamped ? "mt16" : undefined}>
            <VerdictPackView
              pack={packLoad.pack}
              appName={appName}
              canManage={isOwnerOrAdmin(session)}
              onRefine={() => {
                void navigate("/apps/new/glim");
              }}
              onBuildAnyway={() => {
                setNotYetValidatedStamped(true);
              }}
              onDrop={() => {
                // Client-side only (ux spec §1's own "lightweight, immediate delete"
                // consequence has no landed endpoint - see api/validate.ts's header
                // comment); nothing to grace-period on an idea that was never built.
                void navigate("/apps");
              }}
              onContinue={() => {
                void navigate("/apps/new/brand");
              }}
            />
          </div>
        </div>
      )}
    </FocusedFlowShell>
  );
};
